import React from "react";

import { ImageProps, ImageSource } from "./types";
import { IS_PROD, toWebpSrcSet, toWebpUrl } from "./webp";

/**
 * Единый компонент изображения (SEO / Core Web Vitals, задачи #12 и #11).
 *
 * - `alt` обязателен (декоративным — `alt=""`);
 * - `width`/`height` пробрасываются как есть (против CLS) — задавать явно;
 * - по умолчанию `loading="lazy"` + `decoding="async"`; для LCP-картинок — `priority`;
 * - `sources` рендерит `<picture>` с `<source>` (адаптив);
 * - **авто-WebP (#11):** если у локальной картинки есть webp-двойник (см. `webp.ts` + `webpManifest.json`,
 *   генерится `scripts/generate-webp.js`), в `<picture>` добавляется `<source type="image/webp">`.
 *   Оригинал остаётся fallback'ом. Только в прод-сборке (в dev webp-файлов может не быть).
 */

/** Строит webp-`<source>` (по одному на каждый исходный source, либо один из `src`). */
const buildWebpSources = (src: string, sources?: ImageSource[]): ImageSource[] => {
  if (!IS_PROD) return [];

  if (sources?.length) {
    return sources
      .map((source) => {
        const webpSrcSet = toWebpSrcSet(source.srcSet);
        return webpSrcSet
          ? { srcSet: webpSrcSet, media: source.media, type: "image/webp" }
          : null;
      })
      .filter((s): s is ImageSource =>
        s !== null);
  }

  const webpUrl = toWebpUrl(src);
  return webpUrl ? [{ srcSet: webpUrl, type: "image/webp" }] : [];
};

const Image = ({
  src, alt, width, height, priority = false, fetchPriority, sources, ...rest
}: ImageProps) => {
  const img = (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? "eager" : "lazy"}
      // Ниже сгиба — `async`: декодирование не должно блокировать отрисовку страницы.
      // Для LCP-картинки оставляем `auto` (решает браузер): `async` разрешает показать её
      // кадром позже, то есть может отодвинуть сам LCP. Тикет 4564 просит `async` пятым
      // пунктом общего чек-листа, но без замера забирать решение у браузера смысла нет.
      decoding={priority ? "auto" : "async"}
      // в нижнем регистре — React пробрасывает неизвестный ему проп как есть
      fetchpriority={fetchPriority}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    />
  );

  const webpSources = buildWebpSources(src, sources);

  if (sources?.length || webpSources.length) {
    return (
      <picture>
        {/* webp-source(ы) идут первыми — браузер выбирает webp, если поддерживает */}
        {webpSources.map((source) =>
          (
            <source key={`webp-${source.srcSet}`} srcSet={source.srcSet} media={source.media} type={source.type} />
          ))}
        {sources?.map((source) =>
          (
            <source key={source.srcSet} srcSet={source.srcSet} media={source.media} type={source.type} />
          ))}
        {img}
      </picture>
    );
  }

  return img;
};

export default Image;
